import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import appdata from '../../../Services/appdata';
import YearSelector from '../../../Common/Selectors/YearSelectorSingle';
import { creators as ProjectedCashFlowActions } from '../../../Reducers/Reporting/ProjectedCashFlow';
import { printFormat } from '../../../Utils/Functions';
import TopChart from './TopChart';
import MiddleChart from './MiddleChart';
import BottomChart from './BottomChart';

const ProjectedCashFlow = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  const projectedCashFlowSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.projectedCashFlow ?? '');
  const topData = useSelector((state) => state.Reporting.ProjectedCashFlow.topData);
  const middleData = useSelector((state) => state.Reporting.ProjectedCashFlow.middleData);
  const bottomData = useSelector((state) => state.Reporting.ProjectedCashFlow.bottomData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [dimDate, setDimDate] = useState([defaultDimDate]);
  const [label, setLabel] = useState(defaultYear.toString());
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedMiddleItems, setSelectedMiddleItems] = useState([]);
  const [selectedBottomItems, setSelectedBottomItems] = useState([]);
  const [resize, setResize] = useState(false);

  const handleResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);
  
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    setSelectedYears([defaultYear]);
    setLabel(defaultYear.toString());
    setDimDate([defaultDimDate]);
    
    const savedTopItems = appdata.getLocalItem('pcfSelectedTopItems');
    const initialTopItems = savedTopItems || [];
    setSelectedTopItems(initialTopItems);
    appdata.setLocalItem('pcfSelectedTopItems', initialTopItems);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [defaultYear, defaultDimDate, handleResize]);

  const triggerProjectedCashFlowRequests = useCallback(
    ({ years, topItems, allowStorageFallback = false } = {}) => {
      let resolvedYears =
        years && years.length
          ? years
          : selectedYears && selectedYears.length
          ? selectedYears
          : [defaultYear];

      let resolvedTopItems =
        topItems !== undefined ? topItems : selectedTopItems ?? [];

      if (
        allowStorageFallback &&
        (!resolvedTopItems || resolvedTopItems.length === 0)
      ) {
        const storedTopItems = appdata.getLocalItem('pcfSelectedTopItems');
        if (storedTopItems && storedTopItems.length > 0) {
          resolvedTopItems = storedTopItems;
        }
      }

      let dimDateKeys = [];
      if (resolvedTopItems && resolvedTopItems.length > 0) {
        dimDateKeys = resolvedTopItems.map((item) =>
          printFormat.dateYYYYMMDD(
            item.month + 1 >= defaultStartMonth ? item.year - 1 : item.year,
            item.month + 1,
          ),
        );
      }

      if (dimDateKeys.length === 0 && allowStorageFallback) {
        const savedDimDate = appdata.getLocalItem('pcfDimDate');
        if (savedDimDate && savedDimDate.length > 0) {
          dimDateKeys = savedDimDate;
        }
      }

      if (dimDateKeys.length === 0) {
        dimDateKeys = [defaultDimDate];
      }

      setDimDate(dimDateKeys);

      const reportingDateKeys = [defaultDimDate];

      dispatch(
        ProjectedCashFlowActions.projectedcashflowTopRequest(
          resolvedYears,
          reportingDateKeys,
          reportingDateKeys,
        ),
      );
      dispatch(
        ProjectedCashFlowActions.projectedcashflowMiddleRequest(
          resolvedYears,
          dimDateKeys,
          reportingDateKeys,
        ),
      );
      dispatch(
        ProjectedCashFlowActions.projectedcashflowBottomRequest(resolvedYears),
      );
    },
    [
      dispatch,
      defaultDimDate,
      defaultStartMonth,
      defaultYear,
      selectedTopItems,
      selectedYears,
    ],
  );

  useEffect(() => {
    if (!lastFetchedCompany || !lastFetchedDimDate) {      
      triggerProjectedCashFlowRequests({ allowStorageFallback: true });
    }
  }, [
    lastFetchedCompany,
    lastFetchedDimDate,
    triggerProjectedCashFlowRequests,
  ]);

  useEffect(() => {
    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (valuesChanged && lastFetchedCompany && lastFetchedDimDate) {
      triggerProjectedCashFlowRequests({ allowStorageFallback: true });
    }
  }, [
    defaultDimDate,
    selectedCompany,
    lastFetchedCompany,
    lastFetchedDimDate,
    triggerProjectedCashFlowRequests,
  ]);

  const handleYear = useCallback(
    async (event) => {
      if (event.selectedYears.length === 0) {
        toast.warning('Please select the year.');
        return;
      }

      setSelectedYears(event.selectedYears);
      setLabel(event.label);
      setSelectedTopItems([]);
      setSelectedBottomItems([]);
      setSelectedMiddleItems([]);
      appdata.setLocalItem('pcfSelectedTopItems', []);
      triggerProjectedCashFlowRequests({ years: event.selectedYears });
    },
    [],
  );

  const handleFilter = useCallback(async (event) => {
    if (event.selectedTopItems) {
      setSelectedTopItems(event.selectedTopItems);
      appdata.setLocalItem('pcfSelectedTopItems', event.selectedTopItems);
      triggerProjectedCashFlowRequests({ topItems: event.selectedTopItems });
    }

    if (event.dimDate) {
      appdata.setLocalItem('pcfDimDate', event.dimDate);
      setDimDate(event.dimDate);
    }

    if (event.selectedMiddleItems) {
      setSelectedMiddleItems(event.selectedMiddleItems);
    }

    if (event.selectedBottomItems) {
      setSelectedBottomItems(event.selectedBottomItems);
    }
  }, []);

  const calculateChartDimensions = () => {
    let cashFlowGroupNameLengths = [];
    let cashFlowCategoryLengths = [];

    middleData.forEach((data) => {
      if (data.CashFlowGroup) {
        cashFlowGroupNameLengths.push(data.CashFlowGroup.length);
      }
      if (data.CashFlowCategory) {
        cashFlowCategoryLengths.push(data.CashFlowCategory.length);
      }
    });

    return {
      maxCashFlowGroupName: cashFlowGroupNameLengths.length > 0 
        ? Math.max(...cashFlowGroupNameLengths) + 3 
        : 0,
      maxCashFlowCategoryName: cashFlowCategoryLengths.length > 0 
        ? Math.max(...cashFlowCategoryLengths) 
        : 0
    };
  };

  const { maxCashFlowGroupName, maxCashFlowCategoryName } = calculateChartDimensions();

  return (
    <div>
      <div className="wrapper" id="pcf_year_selector">
        {selectedYears.length > 0 ? (
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={handleYear}
          />
        ) : null}
      </div>

      <div>
        <TopChart
          topData={topData}
          selectedYears={selectedYears}
          selectedTopItems={selectedTopItems}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
          updateFilter={handleFilter}
        />
        <MiddleChart          
          middleData={middleData}
          dimDate={dimDate}
          defaultDimDate={defaultDimDate}
          defaultStartMonth={defaultStartMonth}
          selectedTopItems={selectedTopItems}
          selectedMiddleItems={selectedMiddleItems}
          maxCashFlowGroupName={maxCashFlowGroupName}
          maxCashFlowCategoryName={maxCashFlowCategoryName}
          updateFilter={handleFilter}
        />
        <BottomChart
          bottomData={bottomData}
          selectedTopItems={selectedTopItems}
          selectedBottomItems={selectedBottomItems}
          selectedMiddleItems={selectedMiddleItems}
          updateFilter={handleFilter}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
        />
      </div>

      {bubbleFlag && projectedCashFlowSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={projectedCashFlowSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

ProjectedCashFlow.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  bubbleFlag: PropTypes.bool,
};

export default ProjectedCashFlow;
